import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Tooltip from "@mui/material/Tooltip";
import { Button, Form, Modal, Nav, Table } from "react-bootstrap";
import AddMaterialManualModal from "../components/AddMaterialManualModal.jsx";
import { materialPropertyPairsSearchBlob } from "../helpers/materialPropertyPairs";
import {
    effectiveStockUnitPriceUAH,
    effectiveStockUnitPriceUSD,
} from "../lib/materialStockUnitPriceUAH";

/** Кілька слів через пробіл — усі мають входити в об’єднаний текст (тип, колір, властивості…). */
function stockFilterTokens(qRaw) {
    return String(qRaw || "")
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean);
}

/** Заголовок колонки з сортуванням по кліку (як у типових таблицях). */
function SortableTh({
    children,
    columnKey,
    sortKey,
    sortDir,
    onSort,
    className = "",
    headerTextAlign,
    title: thTitle,
}) {
    const active = sortKey === columnKey;
    const indicator = active ? (sortDir === "asc" ? " ▲" : " ▼") : "";
    return (
        <th
            role="button"
            tabIndex={0}
            title={thTitle}
            className={`user-select-none ${className}`.trim()}
            style={{
                cursor: "pointer",
                ...(headerTextAlign ? { textAlign: headerTextAlign } : {}),
            }}
            onClick={() => onSort(columnKey)}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSort(columnKey);
                }
            }}
        >
            {children}
            {indicator}
        </th>
    );
}

const StockTableRow = React.forwardRef(function StockTableRow(props, ref) {
    return <tr ref={ref} {...props} />;
});

function ProducibleTooltipTitle({ entry, typeName, color }) {
    if (!entry) {
        return (
            <span className="small text-muted">
                Наведіть курсор — порахуємо, скільки виробів можна зняти за техкартами
            </span>
        );
    }
    if (entry.loading) {
        return <span className="small">Розрахунок…</span>;
    }
    if (entry.error) {
        return <span className="small text-warning">Не вдалося завантажити</span>;
    }
    const items = entry.items || [];
    if (items.length === 0) {
        return (
            <span className="small text-muted">
                Немає техкарт з цим матеріалом або залишку не вистачить на 1 шт. за нормою
            </span>
        );
    }
    return (
        <div className="small text-start">
            <div className="fw-semibold mb-1 border-bottom pb-1">
                З поточного залишку ({typeName} / {color})
            </div>
            {items.map((it, i) => (
                <div key={`${it.label}-${i}`}>
                    {it.label}: <strong>{it.pieces}</strong> шт.
                </div>
            ))}
        </div>
    );
}

function formatMoney2(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) {
        return "—";
    }
    return n.toLocaleString("uk-UA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

/** Кількість на складі / у русі — до сотих. */
function formatQtyUk2(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) {
        return "—";
    }
    return n.toLocaleString("uk-UA", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
}

function formatSignedQtyUk2(signedQty, unit) {
    const n = Number(signedQty);
    if (!Number.isFinite(n)) {
        return "—";
    }
    const u = unit ? ` ${unit}` : "";
    const sign = n > 0 ? "+" : "";
    return `${sign}${formatQtyUk2(n)}${u}`;
}

function materialMatchesStockFilter(m, qRaw, stockCurrency, usdSellRate, qtyForRow) {
    const tokens = stockFilterTokens(qRaw);
    if (tokens.length === 0) {
        return true;
    }
    const dq = qtyForRow(m);
    const amount = stockLineAmount(m, stockCurrency, usdSellRate, dq);
    const propsBlob = materialPropertyPairsSearchBlob(m.propertyPairs || []);
    const refPrice = m.lastPurchaseDate
        ? lastPurchaseStoredUnitSubtitle(m) || ""
        : cardPurchaseColumnSubtitle(m, usdSellRate) || "";
    const parts = [
        m.typeId?.name,
        m.color,
        m.unit,
        m.note,
        propsBlob,
        String(dq),
        formatQtyUk2(dq),
        m.lastPurchaseDate ? new Date(m.lastPurchaseDate).toLocaleDateString("uk-UA") : "",
        refPrice || "",
        formatMoney2(amount),
        String(amount),
    ];
    const hay = parts.join(" ").toLowerCase();
    return tokens.every((t) => hay.includes(t));
}

function historyRowMatchesFilter(row, qRaw, historyUnit) {
    const q = String(qRaw || "").trim().toLowerCase();
    if (!q) {
        return true;
    }
    const parts = [
        row.at ? new Date(row.at).toLocaleDateString("uk-UA") : "",
        row.label,
        formatSignedQtyUk2(row.signedQty, historyUnit),
        row.detail,
    ];
    return parts.join(" ").toLowerCase().includes(q);
}

/** Ефективні ₴ та $ за одиницю складу (та сама логіка, що для колонки «Сума»). */
function stockEffectiveUnitPrices(m, usdSellRate) {
    const rate = Number(usdSellRate);
    const hasRate = Number.isFinite(rate) && rate > 0;

    let uah = effectiveStockUnitPriceUAH(m);
    if (uah == null || !Number.isFinite(uah)) {
        uah = Number(m.priceUAH);
    }
    if (!Number.isFinite(uah)) {
        uah = 0;
    }
    if (uah === 0 && hasRate) {
        const usde = effectiveStockUnitPriceUSD(m);
        const usdNum =
            usde != null && Number.isFinite(usde) ? usde : Number(m.priceUSD);
        if (Number.isFinite(usdNum)) {
            uah = usdNum * rate;
        }
    }

    let usd = effectiveStockUnitPriceUSD(m);
    if (usd == null || !Number.isFinite(usd)) {
        usd = Number(m.priceUSD);
    }
    if (!Number.isFinite(usd)) {
        usd = 0;
    }
    if (usd === 0 && hasRate) {
        let uahSrc = effectiveStockUnitPriceUAH(m);
        if (uahSrc == null || !Number.isFinite(uahSrc)) {
            uahSrc = Number(m.priceUAH);
        }
        if (Number.isFinite(uahSrc) && uahSrc > 0) {
            usd = uahSrc / rate;
        }
    }

    return { uahPerUnit: uah, usdPerUnit: usd };
}

function lastPurchaseStoredUnitSubtitle(m) {
    const uah = Number(m.lastPurchaseUnitPriceUAH);
    const usd = Number(m.lastPurchaseUnitPriceUSD);
    const bits = [];
    if (Number.isFinite(uah) && uah > 0) {
        bits.push(`${formatMoney2(uah)} ₴`);
    }
    if (Number.isFinite(usd) && usd > 0) {
        bits.push(`${formatMoney2(usd)} $`);
    }
    if (!bits.length) {
        return null;
    }
    return `${bits.join(" · ")}/од.`;
}

function cardPurchaseColumnSubtitle(m, usdSellRate) {
    const { uahPerUnit, usdPerUnit } = stockEffectiveUnitPrices(m, usdSellRate);
    const bits = [];
    if (Number.isFinite(uahPerUnit) && uahPerUnit > 0) {
        bits.push(`${formatMoney2(uahPerUnit)} ₴`);
    }
    if (Number.isFinite(usdPerUnit) && usdPerUnit > 0) {
        bits.push(`${formatMoney2(usdPerUnit)} $`);
    }
    if (!bits.length) {
        return null;
    }
    return `${bits.join(" · ")}/од.`;
}

function MaterialStockLastPurchaseCell({ m, usdSellRate }) {
    const lastPurLine = lastPurchaseStoredUnitSubtitle(m);
    const cardLine = cardPurchaseColumnSubtitle(m, usdSellRate);

    if (m.lastPurchaseDate) {
        return (
            <>
                <div className="text-nowrap text-body">
                    {new Date(m.lastPurchaseDate).toLocaleDateString("uk-UA")}
                </div>
                {lastPurLine ? (
                    <div className="text-muted pt-1" style={{ lineHeight: 1.25 }}>
                        {lastPurLine}
                    </div>
                ) : null}
            </>
        );
    }

    if (cardLine) {
        return (
            <div className="text-muted" style={{ lineHeight: 1.25 }}>
                {cardLine}
            </div>
        );
    }

    return <span className="text-muted">—</span>;
}

/** Вартість рядка на складі в обраній валюті (грн або USD). */
function stockLineAmount(m, currency, usdSellRate, displayQtyOverride) {
    const st =
        displayQtyOverride != null ? Number(displayQtyOverride) : Number(m.stock) || 0;

    if (currency === "UAH") {
        const { uahPerUnit } = stockEffectiveUnitPrices(m, usdSellRate);
        return st * (Number.isFinite(uahPerUnit) ? uahPerUnit : 0);
    }

    const { usdPerUnit } = stockEffectiveUnitPrices(m, usdSellRate);
    return st * (Number.isFinite(usdPerUnit) ? usdPerUnit : 0);
}

/** Округлення як на сервері для рядка корекції (дельта). */
function roundAdjustQty2(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) {
        return 0;
    }
    return Math.round(x * 100) / 100;
}

export default function MaterialsStockPage({
    stockCurrency = "UAH",
    usdSellRate = null,
    urlStockScopeKey = "all",
    onStockScopeNavigate,
}) {

    const [materials, setMaterials] = useState([]);
    const [workshops, setWorkshops] = useState([]);
    const [ourMaterialWorkshopId, setOurMaterialWorkshopId] = useState(null);
    /** "all" — сума по всіх складах (як у material.stock); інакше id цеху. */
    const [stockScope, setStockScope] = useState(urlStockScopeKey);
    const [selectedMaterial, setSelectedMaterial] = useState(null);

    const [showAdjust, setShowAdjust] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [showManualAdd, setShowManualAdd] = useState(false);

    const [historyMovements, setHistoryMovements] = useState([]);
    const [historyUnit, setHistoryUnit] = useState("");
    const [historyFilterQ, setHistoryFilterQ] = useState("");
    const [historySortKey, setHistorySortKey] = useState(null);
    const [historySortDir, setHistorySortDir] = useState("desc");

    const [stockFilterQ, setStockFilterQ] = useState("");
    const [stockSortKey, setStockSortKey] = useState(null);
    const [stockSortDir, setStockSortDir] = useState("asc");

    const [qty, setQty] = useState("");

    const [producibleById, setProducibleById] = useState({});
    const producibleLoadedRef = useRef(new Set());
    const producibleLockRef = useRef(new Set());

    useEffect(() => {
        loadMaterials();
    }, []);

    useEffect(() => {
        setStockScope(urlStockScopeKey);
    }, [urlStockScopeKey]);

    useEffect(() => {
        if (urlStockScopeKey === "all") {
            return;
        }
        if (!workshops.length) {
            return;
        }
        const ok = workshops.some((w) => String(w._id) === String(urlStockScopeKey));
        if (!ok && typeof onStockScopeNavigate === "function") {
            onStockScopeNavigate("all");
        }
    }, [workshops, urlStockScopeKey, onStockScopeNavigate]);

    const changeStockScope = useCallback(
        (k) => {
            if (!k) {
                return;
            }
            setStockScope(k);
            onStockScopeNavigate?.(k);
        },
        [onStockScopeNavigate]
    );

    const loadMaterials = async () => {
        const res = await axios.get("/api/materials/stock-context", { withCredentials: true });
        const data = res.data || {};
        setMaterials(Array.isArray(data.materials) ? data.materials : []);
        setWorkshops(Array.isArray(data.workshops) ? data.workshops : []);
        setOurMaterialWorkshopId(
            data.ourMaterialWorkshopId != null && data.ourMaterialWorkshopId !== ""
                ? String(data.ourMaterialWorkshopId)
                : null
        );
        producibleLoadedRef.current = new Set();
        setProducibleById({});
    };

    const displayQtyForMaterial = useCallback((m) => {
        if (stockScope === "all") {
            return Number(m.stock) || 0;
        }
        const map = m.workshopStocks || {};
        return Number(map[stockScope]) || 0;
    }, [stockScope]);

    const handleStockSort = useCallback((key) => {
        setStockSortKey((prev) => {
            if (prev === key) {
                setStockSortDir((d) => (d === "asc" ? "desc" : "asc"));
                return prev;
            }
            setStockSortDir("asc");
            return key;
        });
    }, []);

    const handleHistorySort = useCallback((key) => {
        setHistorySortKey((prev) => {
            if (prev === key) {
                setHistorySortDir((d) => (d === "asc" ? "desc" : "asc"));
                return prev;
            }
            setHistorySortDir(key === "at" ? "desc" : "asc");
            return key;
        });
    }, []);

    const visibleMaterials = useMemo(() => {
        const filtered = materials.filter((m) =>
            materialMatchesStockFilter(
                m,
                stockFilterQ,
                stockCurrency,
                usdSellRate,
                displayQtyForMaterial
            )
        );
        if (!stockSortKey) {
            return filtered;
        }
        const dir = stockSortDir === "asc" ? 1 : -1;
        const getVal = (m) => {
            switch (stockSortKey) {
                case "material":
                    return String(m.typeId?.name || "").toLowerCase();
                case "color":
                    return String(m.color || "").toLowerCase();
                case "lastPurchase":
                    return m.lastPurchaseDate ? new Date(m.lastPurchaseDate).getTime() : 0;
                case "qty":
                    return Number(displayQtyForMaterial(m)) || 0;
                case "unit":
                    return String(m.unit || "").toLowerCase();
                case "amount":
                    return stockLineAmount(
                        m,
                        stockCurrency,
                        usdSellRate,
                        displayQtyForMaterial(m)
                    );
                default:
                    return "";
            }
        };
        return [...filtered].sort((a, b) => {
            const va = getVal(a);
            const vb = getVal(b);
            if (va < vb) {
                return -1 * dir;
            }
            if (va > vb) {
                return 1 * dir;
            }
            return String(a._id).localeCompare(String(b._id));
        });
    }, [
        materials,
        stockFilterQ,
        stockCurrency,
        usdSellRate,
        stockSortKey,
        stockSortDir,
        displayQtyForMaterial,
    ]);

    const totalStock = useMemo(
        () =>
            visibleMaterials.reduce(
                (acc, m) =>
                    acc +
                    stockLineAmount(
                        m,
                        stockCurrency,
                        usdSellRate,
                        displayQtyForMaterial(m)
                    ),
                0
            ),
        [visibleMaterials, stockCurrency, usdSellRate, displayQtyForMaterial]
    );

    const visibleHistoryRows = useMemo(() => {
        const filtered = historyMovements.filter((row) =>
            historyRowMatchesFilter(row, historyFilterQ, historyUnit)
        );
        if (!historySortKey) {
            return filtered;
        }
        const dir = historySortDir === "asc" ? 1 : -1;
        const getVal = (row) => {
            switch (historySortKey) {
                case "at":
                    return row.at ? new Date(row.at).getTime() : 0;
                case "label":
                    return String(row.label || "").toLowerCase();
                case "signedQty":
                    return Number(row.signedQty) || 0;
                case "detail":
                    return String(row.detail || "").toLowerCase();
                default:
                    return "";
            }
        };
        return [...filtered].sort((a, b) => {
            const va = getVal(a);
            const vb = getVal(b);
            if (va < vb) {
                return -1 * dir;
            }
            if (va > vb) {
                return 1 * dir;
            }
            return 0;
        });
    }, [historyMovements, historyFilterQ, historyUnit, historySortKey, historySortDir]);

    const adjustTargetWorkshopId =
        stockScope === "all" ? ourMaterialWorkshopId || "" : stockScope;

    const prefetchProducible = useCallback(async (id) => {
        if (producibleLoadedRef.current.has(id) || producibleLockRef.current.has(id)) {
            return;
        }
        producibleLockRef.current.add(id);
        try {
            const r = await axios.get(`/api/production/materials/${id}/producible`, {
                withCredentials: true,
            });
            producibleLoadedRef.current.add(id);
            setProducibleById((prev) => ({
                ...prev,
                [id]: { loading: false, loaded: true, items: r.data.items || [] },
            }));
        } catch {
            producibleLoadedRef.current.add(id);
            setProducibleById((prev) => ({
                ...prev,
                [id]: { loading: false, loaded: true, items: [], error: true },
            }));
        } finally {
            producibleLockRef.current.delete(id);
        }
    }, []);

    const handleRowMouseEnter = useCallback(
        (m) => {
            const id = m._id;
            if (producibleLoadedRef.current.has(id)) {
                return;
            }
            setProducibleById((prev) => {
                if (prev[id]?.loaded) {
                    return prev;
                }
                return { ...prev, [id]: { loading: true, items: [] } };
            });
            void prefetchProducible(id);
        },
        [prefetchProducible]
    );

    const loadHistory = async (material) => {
        const res = await axios.get(`/api/materials/${material._id}/history`, {
            withCredentials: true,
        });
        setHistoryMovements(res.data?.movements || []);
        setHistoryUnit(String(res.data?.unit || material.unit || ""));
        setHistoryFilterQ("");
        setHistorySortKey(null);
        setHistorySortDir("desc");
        setSelectedMaterial(material);
        setShowHistory(true);
    };

    const totalSuffix = stockCurrency === "USD" ? "USD" : "грн";
    const stockFilterActive = Boolean(String(stockFilterQ || "").trim());
    const sumColumnTitle =
        stockScope === "all"
            ? "Загальна сума"
            : stockCurrency === "USD"
              ? "Сума на складі ($)"
              : "Сума на складі (₴)";

    const adjustMaterial = async () => {
        if (!selectedMaterial?._id) {
            return;
        }
        const raw = String(qty ?? "")
            .trim()
            .replace(",", ".");
        const target = parseFloat(raw);
        if (!Number.isFinite(target)) {
            window.alert("Вкажіть новий залишок числом.");
            return;
        }
        const current = displayQtyForMaterial(selectedMaterial);
        const delta = roundAdjustQty2(target - current);
        if (delta === 0) {
            setShowAdjust(false);
            setQty("");
            return;
        }

        await axios.post(
            "/api/materials/adjust",
            {
                materialId: selectedMaterial._id,
                qty: delta,
                ...(adjustTargetWorkshopId
                    ? { destinationWorkshopId: adjustTargetWorkshopId }
                    : {}),
            },
            { withCredentials: true }
        );

        setShowAdjust(false);

        setQty("");

        loadMaterials();

    };

    return (

        <div className="production-tab-panel tab-content">

            <div className="mb-3">
                <h5 className="mb-1">Склад матеріалів</h5>
                <p className="text-muted small mb-2">
                    Рядок таблиці — відкрити історію руху; корекція залишку — кнопка «Корекція» (на вкладці «Загалом»
                    змінюється загальний залишок, як і на цехових). Пошук — по типу, кольору, примітці, властивостях,
                    залишку та сумі; кілька слів через
                    пробіл — усі мають збігатися. Сума зверху — по відфільтрованих рядках. Кнопка «+» —{" "}
                    <strong>оприбуткування на склад без закупівлі</strong> (не плутати з вкладкою «Закупки»).
                </p>
                <Form.Label htmlFor="materials-stock-filter-q" className="visually-hidden">
                    Пошук матеріалів на складі
                </Form.Label>
                <div
                    className="d-flex flex-nowrap align-items-stretch gap-2"
                    style={{ minWidth: 0 }}
                >
                    <Form.Control
                        id="materials-stock-filter-q"
                        className="flex-grow-1"
                        style={{ minWidth: 0 }}
                        value={stockFilterQ}
                        onChange={(e) => setStockFilterQ(e.target.value)}
                        placeholder="Тип, колір, примітка, властивості, залишок…"
                        aria-label="Пошук матеріалів на складі"
                    />
                    <Button
                        type="button"
                        variant="primary"
                        className="px-3 flex-shrink-0 align-self-stretch"
                        onClick={() => setShowManualAdd(true)}
                        title="Оприбуткування на склад без закупівлі"
                        aria-label="Оприбуткування на склад без закупівлі"
                    >
                        +
                    </Button>
                </div>
            </div>

            <Form.Group className="d-md-none mb-3 materials-hub-tab-select">
                <Form.Label className="small text-muted mb-1">Показати склад</Form.Label>
                <Form.Select
                    value={stockScope}
                    aria-label="Цех або загальний склад"
                    onChange={(e) => changeStockScope(e.target.value)}
                >
                    <option value="all">Загалом (усі цехи)</option>
                    {workshops.map((w) => (
                        <option key={w._id} value={String(w._id)}>
                            {w.name}
                        </option>
                    ))}
                </Form.Select>
            </Form.Group>

            <div className="materials-hub-nav-scroll d-none d-md-block">
                <Nav
                    variant="tabs"
                    className="mb-3 flex-nowrap materials-hub-tabs"
                    activeKey={stockScope}
                    onSelect={(k) => k && changeStockScope(k)}
                >
                    <Nav.Item>
                        <Nav.Link eventKey="all" title="Сума залишків по всіх цехах">
                            Загалом
                        </Nav.Link>
                    </Nav.Item>
                    {workshops.map((w) => (
                        <Nav.Item key={w._id}>
                            <Nav.Link eventKey={String(w._id)}>{w.name}</Nav.Link>
                        </Nav.Item>
                    ))}
                </Nav>
            </div>

            <h6 className="mb-3">
                {stockScope === "all"
                    ? `Усього (усі цехи): ${formatMoney2(totalStock)} ${totalSuffix}`
                    : `Цех «${
                          workshops.find((x) => String(x._id) === stockScope)?.name || "—"
                      }»: ${formatMoney2(totalStock)} ${totalSuffix}`}
                {stockFilterActive ? (
                    <span className="text-muted fw-normal small ms-1">
                        · {visibleMaterials.length} поз. за фільтром
                    </span>
                ) : null}
            </h6>

            <div className="border rounded purchases-list-wrap materials-hub-table-scroll">
                <Table bordered hover className="materials-hub-table align-middle mb-0">
                <thead className="table-dark text-center">
                <tr>
                    <SortableTh
                        columnKey="material"
                        sortKey={stockSortKey}
                        sortDir={stockSortDir}
                        onSort={handleStockSort}
                    >
                        Матеріал
                    </SortableTh>
                    <SortableTh
                        columnKey="color"
                        sortKey={stockSortKey}
                        sortDir={stockSortDir}
                        onSort={handleStockSort}
                    >
                        Колір
                    </SortableTh>
                    <SortableTh
                        columnKey="lastPurchase"
                        sortKey={stockSortKey}
                        sortDir={stockSortDir}
                        onSort={handleStockSort}
                        className="text-nowrap"
                        title={
                            "Якщо ще не було закупівлі — показуємо орієнтир із картки матеріалу (₴ / $ за одиницю складу). " +
                            "Якщо був рядок у «Закупках» — дата останнього документа та фактична ціна за одиницю з того рядка " +
                            "(сума рядка ÷ кількість, зарахована на склад)."
                        }
                    >
                        Остання закупка
                    </SortableTh>
                    <SortableTh
                        columnKey="qty"
                        sortKey={stockSortKey}
                        sortDir={stockSortDir}
                        onSort={handleStockSort}
                    >
                        Залишок
                    </SortableTh>
                    <SortableTh
                        columnKey="unit"
                        sortKey={stockSortKey}
                        sortDir={stockSortDir}
                        onSort={handleStockSort}
                    >
                        Од
                    </SortableTh>
                    <SortableTh
                        columnKey="amount"
                        sortKey={stockSortKey}
                        sortDir={stockSortDir}
                        onSort={handleStockSort}
                    >
                        {sumColumnTitle}
                    </SortableTh>
                    <th className="text-nowrap">Дії</th>
                </tr>
                </thead>

                <tbody>

                {visibleMaterials.length === 0 ? (
                    <tr>
                        <td
                            colSpan={7}
                            className="text-muted text-center py-4"
                        >
                            {materials.length === 0
                                ? "Немає матеріалів."
                                : "Нічого не знайдено за запитом."}
                        </td>
                    </tr>
                ) : (
                visibleMaterials.map((m) => (
                    <Tooltip
                        key={m._id}
                        followCursor
                        title={
                            <ProducibleTooltipTitle
                                entry={producibleById[m._id]}
                                typeName={m.typeId?.name || "—"}
                                color={m.color || "—"}
                            />
                        }
                        enterDelay={350}
                        enterNextDelay={200}
                        placement="right"
                        componentsProps={{
                            popper: {
                                modifiers: [
                                    {
                                        name: "offset",
                                        options: {
                                            offset: [14, 0],
                                        },
                                    },
                                ],
                            },
                            tooltip: {
                                sx: {
                                    maxWidth: 360,
                                    bgcolor: "var(--bs-body-bg, #fff)",
                                    color: "var(--bs-body-color, #212529)",
                                    border: "1px solid var(--bs-border-color, #dee2e6)",
                                    boxShadow: 2,
                                },
                            },
                        }}
                    >
                        <StockTableRow
                            role="button"
                            className="material-stock-row"
                            style={{ cursor: "pointer" }}
                            onClick={() => loadHistory(m)}
                            onMouseEnter={() => handleRowMouseEnter(m)}
                        >
                            <td className="fw-medium">{m.typeId?.name}</td>

                            <td>{m.color}</td>

                            <td className="small">
                                <MaterialStockLastPurchaseCell m={m} usdSellRate={usdSellRate} />
                            </td>

                            <td>{formatQtyUk2(displayQtyForMaterial(m))}</td>

                            <td>{m.unit}</td>

                            <td>
                                {formatMoney2(
                                    stockLineAmount(
                                        m,
                                        stockCurrency,
                                        usdSellRate,
                                        displayQtyForMaterial(m)
                                    )
                                )}
                            </td>

                            <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                <Button
                                    size="sm"
                                    variant="warning"
                                    onClick={() => {
                                        setSelectedMaterial(m);
                                        const cur = displayQtyForMaterial(m);
                                        setQty(Number.isFinite(cur) ? String(cur) : "");
                                        setShowAdjust(true);
                                    }}
                                >
                                    Корекція
                                </Button>
                            </td>
                        </StockTableRow>
                    </Tooltip>
                ))
                )}

                </tbody>

                </Table>
            </div>

            {/* MODAL CORRECTION */}

            <Modal
                show={showAdjust}
                onHide={() => {
                    setShowAdjust(false);
                    setQty("");
                }}
            >

                <Modal.Header closeButton>

                    <Modal.Title>

                        Корекція: {selectedMaterial?.typeId?.name} / {selectedMaterial?.color}

                    </Modal.Title>

                </Modal.Header>

                <Modal.Body>

                    <p className="text-muted small mb-2">
                        Зараз у колонці «Залишок»
                        {stockScope === "all" ? " (усі цехи разом)" : ""}:{" "}
                        <strong>
                            {selectedMaterial
                                ? `${formatQtyUk2(displayQtyForMaterial(selectedMaterial))} ${selectedMaterial.unit || ""}`.trim()
                                : "—"}
                        </strong>
                    </p>

                    <Form.Group>

                        <Form.Label>Новий залишок</Form.Label>

                        <Form.Control
                            type="number"
                            step="any"
                            value={qty}
                            onChange={(e) => setQty(e.target.value)}
                        />

                    </Form.Group>

                    <p className="text-muted small mb-0 mt-2">
                        Після збереження на сервер піде{" "}
                        <strong>різниця</strong> між новим і поточним значенням (як раніше в історії: + / −).
                        {stockScope === "all" ? (
                            <>
                                {" "}
                                На вкладці «Загалом» змінюється загальний залишок; ту саму величину додає сервер до
                                складу цеху за замовчуванням
                                {ourMaterialWorkshopId
                                    ? ` («${
                                          workshops.find((w) => String(w._id) === ourMaterialWorkshopId)?.name ||
                                          "наш цех"
                                      }»)`
                                    : " (OUR_MATERIAL_WORKSHOP_ID або перший цех)"}
                                .
                            </>
                        ) : null}
                    </p>

                </Modal.Body>

                <Modal.Footer>

                    <Button onClick={adjustMaterial}>
                        Застосувати
                    </Button>

                </Modal.Footer>

            </Modal>


            {/* MODAL HISTORY */}

            <Modal show={showHistory} onHide={() => setShowHistory(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Історія руху — {selectedMaterial?.typeId?.name} / {selectedMaterial?.color}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted small mb-2">
                        <strong>+</strong> закупки та надходження, <strong>−</strong> відшив (поставка з цеху) та
                        списання за нормами техкарт.
                    </p>
                    <Form.Label htmlFor="materials-stock-history-filter" className="visually-hidden">
                        Пошук у історії руху
                    </Form.Label>
                    <Form.Control
                        id="materials-stock-history-filter"
                        className="mb-2"
                        value={historyFilterQ}
                        onChange={(e) => setHistoryFilterQ(e.target.value)}
                        placeholder="Дата, операція, зміна кількості, коментар…"
                        aria-label="Пошук у історії руху"
                    />
                    <div className="border rounded purchases-list-wrap materials-hub-table-scroll">
                        <Table bordered size="sm" className="materials-hub-table mb-0">
                            <thead className="table-light text-center">
                                <tr>
                                    <SortableTh
                                        columnKey="at"
                                        sortKey={historySortKey}
                                        sortDir={historySortDir}
                                        onSort={handleHistorySort}
                                    >
                                        Дата
                                    </SortableTh>
                                    <SortableTh
                                        columnKey="label"
                                        sortKey={historySortKey}
                                        sortDir={historySortDir}
                                        onSort={handleHistorySort}
                                        headerTextAlign="start"
                                    >
                                        Операція
                                    </SortableTh>
                                    <SortableTh
                                        columnKey="signedQty"
                                        sortKey={historySortKey}
                                        sortDir={historySortDir}
                                        onSort={handleHistorySort}
                                    >
                                        Зміна
                                    </SortableTh>
                                    <SortableTh
                                        columnKey="detail"
                                        sortKey={historySortKey}
                                        sortDir={historySortDir}
                                        onSort={handleHistorySort}
                                        headerTextAlign="start"
                                    >
                                        Докладно
                                    </SortableTh>
                                </tr>
                            </thead>
                            <tbody>
                                {historyMovements.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="text-muted text-center py-3">
                                            Записів ще немає
                                        </td>
                                    </tr>
                                ) : visibleHistoryRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="text-muted text-center py-3">
                                            Нічого не знайдено за запитом.
                                        </td>
                                    </tr>
                                ) : (
                                    visibleHistoryRows.map((row, i) => (
                                        <tr key={`${row.sortAt}-${i}`}>
                                            <td className="text-nowrap small">
                                                {row.at
                                                    ? new Date(row.at).toLocaleDateString("uk-UA")
                                                    : "—"}
                                            </td>
                                            <td className="small">{row.label}</td>
                                            <td className="text-nowrap text-center small fw-medium">
                                                {formatSignedQtyUk2(row.signedQty, historyUnit)}
                                            </td>
                                            <td className="small text-muted">{row.detail || "—"}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
            </Modal>

            <AddMaterialManualModal
                show={showManualAdd}
                onHide={() => setShowManualAdd(false)}
                onSaved={loadMaterials}
                defaultExchangeRate={usdSellRate}
                destinationWorkshopId={
                    stockScope === "all" ? ourMaterialWorkshopId || "" : stockScope
                }
            />

        </div>

    );
}